import React from 'react';
import { Lightbulb, AlertTriangle } from 'lucide-react';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { SCHEMAS } from './schemas';
import type { BlocGuide, ContenuRiche } from './guideTypes';

/**
 * Rendu d'un chapitre de l'aide : une suite de blocs.
 *
 * `generateHTML` plutôt qu'une instance d'éditeur, comme `Notes/NoteContentView` — on affiche, on
 * n'édite pas. Les extensions se limitent au socle : le contenu est écrit par l'éditeur du lot 2, pas
 * collé depuis l'extérieur, et une liste d'extensions plus large ne ferait qu'alourdir le rendu.
 */
const EXTENSIONS = [StarterKit.configure({ heading: false })];

const Riche = ({ contenu }: { contenu: ContenuRiche }) => {
    if (!contenu) return null;
    let html: string;
    try {
        html = generateHTML(contenu, EXTENSIONS);
    } catch {
        return null;
    }
    return <div className="prose-notes text-[13.5px] leading-[1.6] text-content text-pretty"
        dangerouslySetInnerHTML={{ __html: html }} />;
};

/** Encadré — « astuce » en vert, « attention » en orange. Deux tons, jamais davantage. */
const Encadre = ({ ton, children }: { ton: 'astuce' | 'attention'; children: React.ReactNode }) => {
    const astuce = ton === 'astuce';
    return (
        <div className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-3 ${
            astuce ? 'border-state-ok/30 bg-state-ok-soft' : 'border-state-warn/30 bg-state-warn-soft'}`}>
            <span className={`mt-[1px] shrink-0 ${astuce ? 'text-state-ok' : 'text-state-warn'}`}>
                {astuce ? <Lightbulb size={15} /> : <AlertTriangle size={15} />}
            </span>
            <div className="min-w-0 flex-1">{children}</div>
        </div>
    );
};

/** Un schéma de la bibliothèque. Une clé inconnue n'affiche rien plutôt qu'un cadre vide : mieux vaut
 *  un trou silencieux qu'un « schéma manquant » sous les yeux d'un client. */
const Schema = ({ cle, legende }: { cle: string; legende?: string }) => {
    const schema = SCHEMAS[cle];
    if (!schema) return null;
    const { Composant } = schema;
    return (
        <figure className="rounded-xl border border-border-soft bg-surface-soft px-3 py-3.5">
            <Composant />
            {legende && (
                <figcaption className="mt-2 text-center text-[11.5px] text-content-muted text-pretty">{legende}</figcaption>
            )}
        </figure>
    );
};

export function GuideBlocs({ blocs }: { blocs: BlocGuide[] }) {
    // Les étapes se numérotent toutes seules, dans l'ordre où elles apparaissent : l'auteur n'écrit
    // jamais « 1 », « 2 », « 3 » à la main — il en oublierait un en insérant une étape au milieu.
    let numeroEtape = 0;
    return (
        <div className="space-y-3.5">
            {blocs.map((bloc, i) => {
                if (bloc.type === 'schema') return <Schema key={i} cle={bloc.cle} legende={bloc.legende} />;
                if (bloc.type === 'astuce' || bloc.type === 'attention') {
                    return <Encadre key={i} ton={bloc.type}><Riche contenu={bloc.contenu} /></Encadre>;
                }
                if (bloc.type === 'etape') {
                    numeroEtape += 1;
                    const n = numeroEtape;
                    return (
                        <div key={i} className="flex items-start gap-3">
                            <span className="mt-[1px] w-[26px] h-[26px] shrink-0 rounded-full bg-brand-600 text-brand-fg text-[12px] font-bold flex items-center justify-center tabular-nums">
                                {n}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="text-[13.5px] font-bold text-content">{bloc.titre}</p>
                                <div className="mt-[3px]"><Riche contenu={bloc.contenu} /></div>
                            </div>
                        </div>
                    );
                }
                return <Riche key={i} contenu={bloc.contenu} />;
            })}
        </div>
    );
}
